import { STATUS_TYPES } from '_atoms/Status';
import type { StatusType } from '_atoms/Status';
import type { SelectOption } from '_atoms/Select/types';

export const PROMOTION_REQUEST_STATUSES = {
  NEW: 'New',
  APPROVED: 'Approved',
  PUBLISHED: 'Published',
  UPDATED: 'Updated',
  DECLINED: 'Declined',
  CANCELLED: 'Cancelled',
  COMPLETED: 'Completed',
  DONE: 'Done',
} as const;

export type PromotionRequestStatus = (typeof PROMOTION_REQUEST_STATUSES)[keyof typeof PROMOTION_REQUEST_STATUSES];

export const PROMOTION_REQUEST_FULL_EDIT_STATUSES: string[] = [PROMOTION_REQUEST_STATUSES.NEW];

export const PROMOTION_REQUEST_LIMITED_EDIT_STATUSES: string[] = [
  PROMOTION_REQUEST_STATUSES.APPROVED,
  PROMOTION_REQUEST_STATUSES.PUBLISHED,
  PROMOTION_REQUEST_STATUSES.UPDATED,
];

export const PROMOTION_REQUEST_EDITABLE_STATUSES: string[] = [
  ...PROMOTION_REQUEST_FULL_EDIT_STATUSES,
  ...PROMOTION_REQUEST_LIMITED_EDIT_STATUSES,
];

export const PROMOTION_REQUEST_COPY_STATUSES: string[] = [
  PROMOTION_REQUEST_STATUSES.NEW,
  PROMOTION_REQUEST_STATUSES.APPROVED,
  PROMOTION_REQUEST_STATUSES.PUBLISHED,
  PROMOTION_REQUEST_STATUSES.UPDATED,
];

export const PROMOTION_REQUEST_APPROVE_DECLINE_STATUSES: string[] = [PROMOTION_REQUEST_STATUSES.NEW];

export const PROMOTION_REQUEST_CANCEL_STATUSES: string[] = PROMOTION_REQUEST_LIMITED_EDIT_STATUSES;

export const PROMOTION_REQUEST_CLOSE_STATUSES: string[] = [
  PROMOTION_REQUEST_STATUSES.CANCELLED,
  PROMOTION_REQUEST_STATUSES.COMPLETED,
];

export const PROMOTION_REQUEST_CONFIRM_PUBLICATION_STATUSES: string[] = [
  PROMOTION_REQUEST_STATUSES.APPROVED,
  PROMOTION_REQUEST_STATUSES.UPDATED,
];

export const PROMOTION_REQUEST_STATUS_TYPES: Record<string, StatusType> = {
  [PROMOTION_REQUEST_STATUSES.NEW]: STATUS_TYPES.GRAY,
  [PROMOTION_REQUEST_STATUSES.APPROVED]: STATUS_TYPES.BLUE,
  [PROMOTION_REQUEST_STATUSES.PUBLISHED]: STATUS_TYPES.GREEN,
  [PROMOTION_REQUEST_STATUSES.UPDATED]: STATUS_TYPES.YELLOW,
  [PROMOTION_REQUEST_STATUSES.DECLINED]: STATUS_TYPES.RED,
  [PROMOTION_REQUEST_STATUSES.CANCELLED]: STATUS_TYPES.GRAY,
  [PROMOTION_REQUEST_STATUSES.COMPLETED]: STATUS_TYPES.GRAY,
  [PROMOTION_REQUEST_STATUSES.DONE]: STATUS_TYPES.GREEN,
};

/* Wire values must match backend exactly — they are the API enum strings. */
export const PROMOTION_SOURCES = {
  CAREER_LEVERX: 'LeverX Career website',
  JOB_BOARDS: 'Job boards (remaining)',
  RABOTA_HH: 'Job boards (rabota.by/hh.ru)',
  PPC: 'Paid social media',
  OWNED_SOCIAL_MEDIA: 'Organic/free social media',
  PROFESSIONAL_SOCIAL_NETWORKING_GROUPS: 'Professional communities',
} as const;

export type PromotionSource = (typeof PROMOTION_SOURCES)[keyof typeof PROMOTION_SOURCES];

export const PROMOTION_PERIOD_TYPES = {
  DAY: 'day',
  MONTH: 'month',
} as const;

export type PromotionPeriodType = (typeof PROMOTION_PERIOD_TYPES)[keyof typeof PROMOTION_PERIOD_TYPES];

export const PAID_SOCIAL_MEDIA_PLATFORMS = {
  META: 'Meta',
  LINKEDIN: 'LinkedIn',
  GOOGLE: 'Google',
} as const;

export type PaidSocialMediaPlatformType =
  (typeof PAID_SOCIAL_MEDIA_PLATFORMS)[keyof typeof PAID_SOCIAL_MEDIA_PLATFORMS];

export const PAID_SOCIAL_MEDIA_PLATFORMS_OPTIONS: SelectOption[] = Object.values(PAID_SOCIAL_MEDIA_PLATFORMS).map(
  value => ({
    value,
    label: value,
  })
);

export const TRANSLATION_LANGUAGES = ['English', 'German', 'Polish', 'Russian'] as const;

export const PROMOTION_REQUEST_STATUS_OPTIONS: SelectOption[] = Object.values(PROMOTION_REQUEST_STATUSES).map(
  value => ({ value, label: value })
);

export const PROMOTION_SOURCE_OPTIONS: SelectOption[] = Object.values(PROMOTION_SOURCES).map(value => ({
  value,
  label: value,
}));

export const TRANSLATION_LANGUAGE_OPTIONS: SelectOption[] = TRANSLATION_LANGUAGES.map(value => ({
  value,
  label: value,
}));

export const DURATION_LIMITS: Record<PromotionPeriodType, number> = {
  [PROMOTION_PERIOD_TYPES.DAY]: 29,
  [PROMOTION_PERIOD_TYPES.MONTH]: 12,
};

export const durationOptions = (periodType?: PromotionPeriodType): SelectOption[] => {
  const max = periodType ? DURATION_LIMITS[periodType] : 0;
  return Array.from({ length: max }, (_, index) => ({ value: String(index + 1), label: String(index + 1) }));
};

/* Source → default promotion period prefilled on selection. `Job boards` has no default. */
export const SOURCE_PERIOD_DEFAULTS: Record<string, { periodType: PromotionPeriodType; duration: number }> = {
  [PROMOTION_SOURCES.CAREER_LEVERX]: { periodType: PROMOTION_PERIOD_TYPES.MONTH, duration: 3 },
  [PROMOTION_SOURCES.RABOTA_HH]: { periodType: PROMOTION_PERIOD_TYPES.MONTH, duration: 1 },
  [PROMOTION_SOURCES.JOB_BOARDS]: { periodType: PROMOTION_PERIOD_TYPES.MONTH, duration: 1 },
  [PROMOTION_SOURCES.PPC]: { periodType: PROMOTION_PERIOD_TYPES.MONTH, duration: 1 },
  [PROMOTION_SOURCES.OWNED_SOCIAL_MEDIA]: { periodType: PROMOTION_PERIOD_TYPES.MONTH, duration: 1 },
  [PROMOTION_SOURCES.PROFESSIONAL_SOCIAL_NETWORKING_GROUPS]: {
    periodType: PROMOTION_PERIOD_TYPES.MONTH,
    duration: 1,
  },
};
